import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useGlobalSlice } from "app/slice";
import { selectIsAppView } from "app/slice/selectors";
import {
  buildPathnameWithSearch,
  getAppViewQueryParam,
  stripAppViewFromSearch,
} from "utils/app-view-session";

/** Persist `?appView=true` and strip it from the URL (call once at layout root). */
export function useSyncAppViewFromUrl(): void {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { actions } = useGlobalSlice();

  useEffect(() => {
    const appViewParam = getAppViewQueryParam(location.search);
    if (appViewParam) {
      dispatch(actions.setAppView(appViewParam));
    }

    const strippedSearch = stripAppViewFromSearch(location.search);
    const currentSearch = location.search.startsWith("?")
      ? location.search.slice(1)
      : location.search;

    if (strippedSearch === currentSearch) {
      return;
    }

    navigate(
      buildPathnameWithSearch(location.pathname, strippedSearch),
      { replace: true },
    );
  }, [actions, dispatch, location.pathname, location.search, navigate]);
}

/** Active in-app WebView chrome hiding (store + transient URL before strip). */
export function useIsAppViewActive(): string | null {
  const isAppView = useSelector(selectIsAppView);
  const { search } = useLocation();
  return isAppView || getAppViewQueryParam(search);
}

/** Layout root: sync URL → session/Redux, then return whether app view is active. */
export function useAppViewMode(): string | null {
  useSyncAppViewFromUrl();
  return useIsAppViewActive();
}
